import React from 'react';
import { Router } from '@uhg-abyss/web/ui/Router';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '../../auth';
import { SharedDialog } from '../../common/SharedDialog';
import { TWO_STATE_SORTING_TABLE_CONFIG } from '../../common/dataTable/twoStateSorting';
import { reportsApi } from './api/reportsApi';
import {
  REPORT_TEXT_SEARCH_MIN_LENGTH,
  useReportsForm,
} from './hooks/useReportsForm';
import { filterReportResults } from './reportFilters';
import type { StatusReportRecord } from './reportFilters';
import type { DownloadSearchType } from './api/reportsApi';

// ─── Styled helpers ───────────────────────────────────────────────────────────

const Page = styled('section', {
  padding: '0',
  display: 'grid',
  gap: '1rem',
});

const SearchPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '1.25rem',
  boxShadow: '0 8px 20px rgba(0, 36, 77, 0.06)',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

const SearchPanelContent = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const InputRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
});

const InputGroup = styled('div', {
  flex: '1 1 220px',
  minWidth: '180px',
  display: 'grid',
  gap: '0.35rem',
});

const TableWrapper = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  padding: '0',
  backgroundColor: '$white',
  '& .abyss-data-table-root': { width: '100%', minWidth: 0 },
  '& table': { width: '100%', tableLayout: 'auto' },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '& .abyss-data-table-scroll': { overflowX: 'auto', overflowY: 'hidden' },
  '& .abyss-data-table-header-content-container': {
    display: 'none !important',
  },
  // Remove top padding/margin above pagination
  '& .abyss-data-table-pagination': { marginTop: 0, paddingTop: 0 },
});

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const FieldLabel = styled('label', {
  display: 'block',
  color: INPUT_LABEL_COLOR,
  fontWeight: 600,
  lineHeight: 1.2,
});

const SearchInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  height: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  padding: '0 0.75rem',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const selectInputLabelCss = {
  'abyss-select-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-select-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-select-input-container': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
  },
  'abyss-select-input-selection-container': {
    minHeight: '2.5rem',
    padding: '0 0.75rem',
    fontSize: '0.95rem',
  },
};

const ErrorCell = styled('td', {
  color: '#c0392b',
  padding: '1rem',
  textAlign: 'center',
});

const HeaderWithHelp = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  whiteSpace: 'normal',
  lineHeight: 1.2,
  verticalAlign: 'middle',
});

const StatusTooltipContent = styled('div', {
  lineHeight: 1.5,
  '& > div + div': {
    marginTop: '0.25rem',
  },
});

const HelpButton = styled('button', {
  width: '1rem',
  height: '1rem',
  minWidth: '1rem',
  minHeight: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '9999px',
  border: '1px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
  backgroundColor: '$web.semantic.color.surface.interactive.standards.rest.default.primary',
  color: '$web.semantic.color.text.label.cta.primary-alt',
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0,
  transform: 'translateY(0)',
  '&:hover': {
    backgroundColor: '$web.semantic.color.surface.interactive.standards.hover.default.primary',
  },
  '&:active': {
    backgroundColor: '$web.semantic.color.surface.interactive.standards.active.default.primary',
  },
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const EXTERNAL_SEARCH_OPTIONS = [
  { label: 'Producer Name', value: 'producerName' },
  { label: 'Agent Name', value: 'agentName' },
];

const INTERNAL_SEARCH_OPTIONS = [
  ...EXTERNAL_SEARCH_OPTIONS,
];

const MARKET_SEGMENT_SECONDARY_OPTIONS = [
  { label: 'Producer Name', value: 'producerName' },
  { label: 'Agent Name', value: 'writingAgentName' },
  { label: 'Sales Office Name', value: 'salesOffice' },
];

const SALES_OFFICE_SECONDARY_OPTIONS = [
  { label: 'Producer Name', value: 'producerName' },
  { label: 'Agent Name', value: 'writingAgentName' },
  { label: 'Market Segment', value: 'marketSegment' },
];

const SEARCH_TYPE_PASCAL: Record<string, string> = {
  producerName: 'ProducerName',
  agentName: 'AgentName',
  marketSegment: 'MarketSegment',
  salesOfficeName: 'SalesOfficeName',
};

const STATUS_TOOLTIP = (
  <StatusTooltipContent>
    <div>
      <strong>Open:</strong> RFI is not started.
    </div>
    <div>
      <strong>In Process:</strong> RFI is started, but responses have not been submitted to UnitedHealthcare.
    </div>
    <div>
      RFIs with status of In Process are considered incomplete and will not be included in the submission to CMS.
    </div>
    <div>
      <strong>Complete:</strong> RFI has been successfully submitted to UnitedHealthcare.
    </div>
    <div>Note: Responses can be modified until the data collection period ends.</div>
  </StatusTooltipContent>
);

const MARKET_SEGMENT_TOOLTIP =
  'Market segment should be used to narrow a search to a more specific segment.';

const SALES_OFFICE_TOOLTIP =
  'Sales Office Name should be used to narrow search to specific markets.';

// ─── Column header with info icon tooltip ─────────────────────────────────────

function ColumnHeaderWithTooltip({
  label,
  tooltip,
}: {
  label: string;
  tooltip: React.ReactNode;
}) {
  return (
    <HeaderWithHelp>
      {label}
      <Tooltip
        content={tooltip}
        position="top"
        variant="white"
        delayDuration={200}
      >
        <HelpButton
          aria-label={`${label} info`}
          type="button"
        >
          ?
        </HelpButton>
      </Tooltip>
    </HeaderWithHelp>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ReportsTableRow extends StatusReportRecord {
  _rowId: string;
  [key: string]: unknown;
}

// eslint-disable-next-line complexity -- The page composes independent conditional search and results views.
export const Reports = () => {
  const { session } = useAuth();
  const isInternalUser = session?.authType === 'INTERNAL';

  const form = useReportsForm();

  const [records, setRecords] = React.useState<StatusReportRecord[]>([]);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const [showDownloadDialog, setShowDownloadDialog] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const searchTypeOptions = isInternalUser
    ? INTERNAL_SEARCH_OPTIONS
    : EXTERNAL_SEARCH_OPTIONS;

  // Load market segments when switching to marketSegment search type
  React.useEffect(() => {
    if (
      form.searchType === 'marketSegment' &&
      form.marketSegmentItems.length === 0
    ) {
      form.loadMarketSegments(() => {
        return reportsApi.getMarketSegments().then((res) => {
          return res.marketSegments;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.searchType]);

  // Secondary filter dropdown options
  const secondaryOptions =
    form.searchType === 'marketSegment'
      ? MARKET_SEGMENT_SECONDARY_OPTIONS
      : form.searchType === 'salesOfficeName'
        ? SALES_OFFICE_SECONDARY_OPTIONS
        : [];

  // Show secondary controls for internal users on market segment / sales office searches,
  // once a primary value is selected.
  const showSecondaryControls =
    isInternalUser &&
    (form.searchType === 'marketSegment' ||
      form.searchType === 'salesOfficeName') &&
    form.primaryValue.trim().length > 0;

  const primaryValueLabel =
    form.searchType === 'producerName'
      ? 'Producer Name'
      : form.searchType === 'agentName'
        ? 'Agent Name'
        : 'Sales Office Name';

  // Client-side filtered display records
  const displayedRecords = React.useMemo(() => {
    return filterReportResults(records, form.secondaryValue, isInternalUser);
  }, [records, form.secondaryValue, isInternalUser]);

  const tableRows = React.useMemo<ReportsTableRow[]>(() => {
    return displayedRecords.map((r) => {
      return {
        ...r,
        _rowId: `${r.employerGroupName}|${r.producerId}|${r.writingAgentId}`,
      };
    });
  }, [displayedRecords]);

  // ─── Search handler ───────────────────────────────────────────────────────

  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      let result: { records: StatusReportRecord[] };

      if (form.searchType === 'producerName') {
        result = await reportsApi.getByProducerName(form.primaryValue);
      } else if (form.searchType === 'agentName') {
        result = await reportsApi.getByAgentName(form.primaryValue);
      } else if (form.searchType === 'marketSegment') {
        result = await reportsApi.getByMarketSegmentFilter(
          form.primaryValue,
          form.secondaryType,
          form.secondaryValue
        );
      } else {
        // salesOfficeName
        result = await reportsApi.getBySalesOfficeFilter(
          form.primaryValue,
          form.secondaryType,
          form.secondaryValue
        );
      }

      setRecords(result.records);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && form.isSearchValid && !isLoading) {
      void handleSearch();
    }
  };

  // ─── Download handler ─────────────────────────────────────────────────────

  const triggerDownload = async (includeInternalFields: 'true' | 'false') => {
    setIsDownloading(true);
    try {
      const blob = await reportsApi.downloadReport({
        searchType: form.searchType as DownloadSearchType,
        searchValue: form.primaryValue,
        filterType: form.secondaryType || undefined,
        filterValue: form.secondaryValue || undefined,
        includeInternalFields,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const pascal = SEARCH_TYPE_PASCAL[form.searchType] ?? form.searchType;
      link.href = url;
      link.download = `EmployerGroupsFor${pascal}_${form.primaryValue}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
      setShowDownloadDialog(false);
    }
  };

  const handleDownload = () => {
    if (isInternalUser) {
      setShowDownloadDialog(true);
    } else {
      void triggerDownload('false');
    }
  };

  // ─── Table columns ────────────────────────────────────────────────────────

  const columns = React.useMemo(() => {
    const allColumns = [
      createColumn<ReportsTableRow>({
        id: 'producerId',
        accessorKey: 'producerId',
        header: 'Producer ID',
        size: 100,
        minSize: 80,
        enableSorting: true,
      }),
      createColumn<ReportsTableRow>({
        id: 'producerName',
        accessorKey: 'producerName',
        header: 'Producer Name',
        enableSorting: true,
      }),
      createColumn<ReportsTableRow>({
        id: 'writingAgentId',
        accessorKey: 'writingAgentId',
        header: 'Agent ID',
        size: 100,
        minSize: 80,
        enableSorting: true,
      }),
      createColumn<ReportsTableRow>({
        id: 'writingAgentName',
        accessorKey: 'writingAgentName',
        header: 'Agent Name',
        enableSorting: true,
      }),
      createColumn<ReportsTableRow>({
        id: 'employerGroupName',
        accessorKey: 'employerGroupName',
        header: 'Employer Group Name',
        enableSorting: true,
      }),
      ...(isInternalUser
        ? [
            createColumn<ReportsTableRow>({
              id: 'marketSegment',
              accessorKey: 'marketSegment',
              header: () => {
                return (
                  <ColumnHeaderWithTooltip
                    label="Market Segment"
                    tooltip={MARKET_SEGMENT_TOOLTIP}
                  />
                );
              },
              enableSorting: true,
            }),
            createColumn<ReportsTableRow>({
              id: 'salesOffice',
              accessorKey: 'salesOffice',
              header: () => {
                return (
                  <ColumnHeaderWithTooltip
                    label="Sales Office Name"
                    tooltip={SALES_OFFICE_TOOLTIP}
                  />
                );
              },
              enableSorting: true,
            }),
          ]
        : []),
      createColumn<ReportsTableRow>({
        id: 'status',
        accessorKey: 'status',
        header: () => {
          return (
            <ColumnHeaderWithTooltip
              label="RFI Status"
              tooltip={STATUS_TOOLTIP}
            />
          );
        },
        enableSorting: true,
        cell: (context: { row: { original: ReportsTableRow } }) => {
          const val = context.row.original.status;
          return val === null ? 'Open' : val;
        },
      }),
    ];
    return allColumns;
  }, [isInternalUser]);

  const tableState = useDataTable<ReportsTableRow>({
    rowIdKey: '_rowId',
    initialData: tableRows,
    initialColumns: columns,
    tableConfig: TWO_STATE_SORTING_TABLE_CONFIG,
    paginationConfig: { enablePagination: true, pageSizeOptions: [10, 25, 50] },
  });

  React.useEffect(() => {
    tableState.setData(tableRows, true);
  }, [tableRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <Router.MetaTags title="Reports" />
      <Page>
      {/* Search panel */}
      <SearchPanel>
        <SearchPanelContent>
          <Heading level={3} size="md" textAlign="left">
            Reports
          </Heading>

          <InputRow>
            <InputGroup style={{ flex: '0 1 260px' }}>
              <SelectInput
                label="Search Type"
                value={form.searchType}
                options={searchTypeOptions}
                css={selectInputLabelCss}
                onChange={(value) => {
                  return form.setSearchType(value as string);
                }}
              />
            </InputGroup>

            {/* Primary value — dropdown for marketSegment, text for all others */}
            {form.searchType === 'marketSegment' ? (
              <InputGroup>
                <SelectInput
                  label="Market Segment"
                  value={form.primaryValue}
                  options={form.marketSegmentItems}
                  isDisabled={form.loadingMarketSegments}
                  css={selectInputLabelCss}
                  onChange={(value) => {
                    return form.setPrimaryValue(value as string);
                  }}
                />
              </InputGroup>
            ) : (
              <InputGroup>
                <FieldLabel htmlFor="reports-primary-value">{primaryValueLabel}</FieldLabel>
                <SearchInput
                  id="reports-primary-value"
                  type="text"
                  aria-label={primaryValueLabel}
                  value={form.primaryValue}
                  onChange={(e) => {
                    return form.setPrimaryValue(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={`min ${REPORT_TEXT_SEARCH_MIN_LENGTH} characters`}
                />
              </InputGroup>
            )}

            {/* Secondary controls — internal only, market segment or sales office */}
            {showSecondaryControls && (
              <React.Fragment>
                <InputGroup style={{ flex: '0 1 240px' }}>
                  <SelectInput
                    label="Filter By"
                    value={form.secondaryType}
                    options={secondaryOptions}
                    css={selectInputLabelCss}
                    onChange={(value) => {
                      return form.setSecondaryType(value as string);
                    }}
                  />
                </InputGroup>

                {form.secondaryType.trim().length > 0 && (
                  <InputGroup>
                    <FieldLabel htmlFor="reports-secondary-value">Filter Value</FieldLabel>
                    <SearchInput
                      id="reports-secondary-value"
                      type="text"
                      aria-label="Filter Value"
                      value={form.secondaryValue}
                      onChange={(e) => {
                        return form.setSecondaryValue(e.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  </InputGroup>
                )}
              </React.Fragment>
            )}

            <div
              style={{
                flex: '0 0 auto',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                marginBottom: 'calc(0.35rem)',
              }}
            >
              <Button
                variant={form.isSearchValid && !isLoading ? 'filled' : 'outline'}
                size="sm"
                isDisabled={!form.isSearchValid || isLoading}
                onClick={handleSearch}
              >
                {isLoading ? 'Searching…' : 'Search'}
              </Button>

              <Button
                variant={displayedRecords.length > 0 && !isDownloading ? 'filled' : 'outline'}
                size="sm"
                isDisabled={displayedRecords.length === 0 || isDownloading}
                onClick={handleDownload}
              >
                {isDownloading ? 'Downloading…' : 'Download'}
              </Button>
            </div>
          </InputRow>
        </SearchPanelContent>
      </SearchPanel>

      {/* Results table */}
      {hasSearched &&
        (isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '2rem',
            }}
          >
            <LoadingSpinner
              isLoading
              ariaLoadingLabel="Searching records"
              size="lg"
            />
          </div>
        ) : (
          <TableWrapper>
            {error ? (
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <ErrorCell colSpan={isInternalUser ? 8 : 6}>
                      An error occurred while fetching data
                    </ErrorCell>
                  </tr>
                </tbody>
              </table>
            ) : displayedRecords.length === 0 ? (
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <ErrorCell colSpan={isInternalUser ? 8 : 6}>
                      No records found with search criteria
                    </ErrorCell>
                  </tr>
                </tbody>
              </table>
            ) : (
              <DataTable<ReportsTableRow> tableState={tableState} title={''}>
                <DataTable.Pagination variant="minimal" showPageSizeDropdown />
                <DataTable.Table />
              </DataTable>
            )}
          </TableWrapper>
        ))}

      {/* Download confirmation dialog (internal users only) */}
      <SharedDialog
        mode="confirm"
        ariaLabel="Export report options"
        isOpen={showDownloadDialog}
        onClose={() => {
          return setShowDownloadDialog(false);
        }}
        title="Export Report"
        description="Do you want to include the Market Segment and Sales Office Name fields in your exported report?"
        primaryAction={{
          label: 'Yes',
          onClick: () => {
            void triggerDownload('true');
          },
          disabled: isDownloading,
        }}
        secondaryAction={{
          label: 'No',
          variant: 'outline',
          onClick: () => {
            void triggerDownload('false');
          },
          disabled: isDownloading,
        }}
      />
      </Page>
    </React.Fragment>
  );
};
